import {merge, Observable} from 'rxjs';
import {delay, filter, map, scan} from 'rxjs/operators';
import {spotOrderUtil} from '@/helper/order-spot';
import {logout$} from '@/user/auth';
import {uiMsg$} from '@/ui';

interface Orders<T> {
  open: T[];
  closed: T[];
}

interface OrderRawBase {
  orderID: string;
}

type Cache<Raw> = Map<string, Raw>;
type Action<Raw> = (map: Cache<Raw>) => Cache<Raw>;

export interface FUOrderConditionalRemove {
  type: 'order-conditional-remove';
  orderID: string;
}
export function createConditionalOrdersCache$<OrderRaw extends OrderRawBase>(
  snapshotOrders$: Observable<Orders<OrderRaw | unknown>>,
  incrementalOrders$: Observable<Orders<OrderRaw | unknown>>
): Observable<Map<string, OrderRaw>> {
  const resetAction$ = logout$.pipe(
    map(
      (): Action<OrderRaw> => (cache: Cache<OrderRaw>) => {
        cache.clear();
        return cache;
      }
    )
  );
  const removeAction$ = uiMsg$.pipe(
    filter((o) => o.type === 'order-conditional-remove'),
    map(
      (data: FUOrderConditionalRemove): Action<OrderRaw> =>
        (cache: Cache<OrderRaw>) => {
          cache.delete(data.orderID);
          return cache;
        }
    )
  );
  const snapshotAction$ = snapshotOrders$.pipe(
    map((o: Orders<OrderRaw>) => o.open.filter((order: OrderRaw) => isDisplayOrder(order, 'snapshot'))),
    map((orders: OrderRaw[]): Action<OrderRaw> => {
      return function (cache: Cache<OrderRaw>) {
        cache.clear();
        orders.forEach((o: OrderRaw) => cache.set(o.orderID, o));
        return cache;
      };
    })
  );

  const updateAction$ = incrementalOrders$.pipe(
    map((o: Orders<OrderRaw>) =>
      [...o.open, ...o.closed].filter((order: OrderRaw) => isDisplayOrder(order, 'incremental'))
    ),
    filter((orders: OrderRaw[]) => orders.length > 0),
    map((orders: OrderRaw[]): Action<OrderRaw> => {
      return function (cache: Cache<OrderRaw>) {
        orders.forEach((o: OrderRaw) => cache.set(o.orderID, o));
        return cache;
      };
    })
  );

  const remoteRemoveAction$ = incrementalOrders$.pipe(
    map((o: Orders<OrderRaw>) => o.closed.filter((order: OrderRaw) => isDisplayOrder(order, 'incremental'))),
    filter((orders: OrderRaw[]) => orders.length > 0),
    map((orders: OrderRaw[]): Action<OrderRaw> => {
      return function (cache: Cache<OrderRaw>) {
        orders.forEach((o: OrderRaw) => cache.delete(o.orderID));
        return cache;
      };
    }),
    delay(5000)
  );

  return merge(snapshotAction$, updateAction$, removeAction$, resetAction$, remoteRemoveAction$).pipe(
    scan((p: Cache<OrderRaw>, c: Action<OrderRaw>): Cache<OrderRaw> => c(p), new Map<string, OrderRaw>())
  );
}

function isDisplayOrder(order, type) {
  const isConditional = spotOrderUtil.isConditionalOrder(order);

  if (!isConditional) {
    return false;
  }

  const {execStatus, ordStatus} = order;

  const acceptable = !['ReplaceRejected', 'CancelRejected'].includes(execStatus);

  if (!acceptable) {
    return false;
  }

  if (type === 'snapshot') {
    // 不显示已结束的订单
    return ['New', 'PartiallyFilled', 'Untriggered'].includes(ordStatus);
  }

  if (type === 'incremental') {
    return ['New', 'PartiallyFilled', 'Untriggered', 'Filled', 'Deactivated', 'Canceled', 'Rejected'].includes(
      ordStatus
    );
  }

  return false;
}
